import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { PMDialog } from "./PMDialog";
import axios from "../../api/axios";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

/**
 * VatTypeSelector - A reusable component for selecting VAT types
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Callback when dialog closes
 * @param {Function} props.onSelect - Callback when a VAT type is selected (receives vatType object)
 * @param {number} props.selectedVatTypeId - Currently selected VAT type ID
 * @param {string} props.title - Optional custom title for the dialog
 * @param {string} props.description - Optional custom description for the dialog
 */
export const VatTypeSelectorDialog = ({ 
  open, 
  onClose, 
  onSelect, 
  selectedVatTypeId,
  title,
  description
}) => {
  const { t } = useTranslation();
  const [vatTypes, setVatTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (open) {
      fetchVatTypes();
    } else {
      // Reset search when dialog closes
      setSearchQuery("");
    }
  }, [open]);

  const fetchVatTypes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/vat_types`);
      const raw = response.data;
      const types = Array.isArray(raw) ? raw : raw?.vat_types || [];
      setVatTypes(types);
    } catch (err) {
      console.error("Error fetching vat types:", err);
      setVatTypes([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredVatTypes = vatTypes.filter((vatType) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      (vatType.display_name || "").toLowerCase().includes(query) ||
      (vatType.name || "").toLowerCase().includes(query) ||
      (vatType.number || "").toString().includes(query) ||
      (vatType.percentage || "").toString().includes(query)
    );
  });

  const handleSelect = (vatType) => {
    if (onSelect) {
      onSelect(vatType);
    }
    onClose();
  };

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title={title || t("report.price_lines.select_vat_type")}
      description={description || t("report.price_lines.select_vat_type_description")}
    >
      <div className="mt-4">
        {/* Search input */}
        <div className="mb-4">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("report.price_lines.search_vat_types")}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>
        </div>

        {/* Loading state */}
        {loading && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-500">{t("Loading...")}</p>
          </div>
        )}

        {/* VAT Types list */}
        {!loading && (
          <div className="max-h-96 overflow-y-auto">
            {filteredVatTypes.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {searchQuery
                  ? t("report.price_lines.no_vat_types_found")
                  : t("report.price_lines.no_vat_types_available")}
              </div>
            ) : (
              <div className="space-y-1">
                {filteredVatTypes.map((vatType) => (
                  <button
                    key={vatType.id}
                    onClick={() => handleSelect(vatType)}
                    className={`w-full text-left px-4 py-3 rounded-md border transition-colors ${
                      selectedVatTypeId === vatType.id
                        ? "bg-blue-50 border-blue-500 text-blue-900"
                        : "bg-white border-gray-200 hover:bg-gray-50 text-gray-900"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="font-medium">
                          {vatType.display_name || vatType.name || `VAT Type ${vatType.id}`}
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                          {vatType.percentage}% {vatType.number && `• Nr. ${vatType.number}`}
                        </div>
                      </div>
                      {selectedVatTypeId === vatType.id && (
                        <div className="ml-4">
                          <svg
                            className="h-5 w-5 text-blue-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </PMDialog>
  );
};

/**
 * VatTypeSelectorButton - A button component that opens the VAT type selector dialog
 * 
 * @param {Object} props
 * @param {Object} props.selectedVatType - Currently selected VAT type object
 * @param {Function} props.onSelect - Callback when a VAT type is selected
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.disabled - Whether the button is disabled
 * @param {string} props.placeholder - Placeholder text when no VAT type is selected
 */
export const VatTypeSelectorButton = ({
  selectedVatType,
  onSelect,
  className = "",
  disabled = false,
  placeholder
}) => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);

  const displayText = selectedVatType
    ? `${selectedVatType.display_name || selectedVatType.name} (${selectedVatType.percentage}%)`
    : (placeholder || t("report.price_lines.select_vat_type"));

  const fullInfo = selectedVatType
    ? `${selectedVatType.display_name || selectedVatType.name} - ${selectedVatType.percentage}%${selectedVatType.number ? ` - Nr. ${selectedVatType.number}` : ""}`
    : "";

  return (
    <>
      <button
        type="button"
        onClick={() => setDialogOpen(true)}
        disabled={disabled}
        className={`w-full px-3 py-2 text-left border border-gray-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 hover:bg-gray-50 ${className} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        title={fullInfo}
      >
        <div className="truncate" title={fullInfo}>
          {displayText}
        </div>
      </button>

      <VatTypeSelectorDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSelect={(vatType) => {
          if (onSelect) {
            onSelect(vatType);
          }
        }}
        selectedVatTypeId={selectedVatType?.id}
      />
    </>
  );
};

